//libraries

import { useQuery } from "@apollo/client";
import { bylineOf, type Byline } from "components/website/authorByline";
import "../css/Website.scss";
import styles from "./Blog.module.scss";

//Components
import Footer from "../components/Footer";

import Navigation from "../components/navigation/Navigation";

import Icon from "components/Icons/Icon";
import { BLOG_LIST } from "graphql/queries/explore";
import { t } from "i18n/index";
import { useMarketingLang } from "i18n/useMarketingLang";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { getBlogTranslations } from "../marketing/i18n/pages/blog";

interface BlogInitialData {
	spotlight?: any;
	articles?: any[];
	categories?: any[];
	total?: number;
}

interface BlogProps {
	/** Server-side pre-fetched data injected via SSR. When absent (SPA path), falls back to client fetch. */
	initialData?: BlogInitialData;
	/** Locale from the route (`/nl/blog` → "nl"), passed by MarketingShell. Drives the UI-string translations. */
	lang?: string;
}

// Same Stippl "S" glyph used elsewhere in the marketing site (BlogBanner.tsx).
const S_MARK_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

function SDecor({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 81 108" className={className} aria-hidden>
			<path d={S_MARK_PATH} />
		</svg>
	);
}

type ChunkyIcon = React.ComponentProps<typeof Icon>["name"];
type AccentKey = "green" | "blue" | "purple" | "forest" | "pink" | "amber";

const ACCENT_CLASS: Record<AccentKey, string> = {
	green: styles.accentGreen,
	blue: styles.accentBlue,
	purple: styles.accentPurple,
	forest: styles.accentForest,
	pink: styles.accentPink,
	amber: styles.accentAmber,
};

// Tilted colour tile with a white chip and black icon in front — the CountryPage motif.
function IconTile({ icon, bgClass }: { icon: ChunkyIcon; bgClass: string }) {
	return (
		<span className={styles.iconTile}>
			<span className={`${styles.iconTileBg} ${bgClass}`} />
			<span className={styles.iconTileChip}>
				<Icon name={icon} fill="currentColor" size={17} />
			</span>
		</span>
	);
}

type Card = { id: string; name: string; img: string; icon: ChunkyIcon; accent: AccentKey };

// ── "Where to?" region art (hero tab) ─────────────────────────────────────
// The image/icon/accent are curated per region and have nothing to do with the corpus, so they
// stay a fixed lookup. Which of these EIGHT cards actually renders is a separate, data-driven
// question — see `regionCards` below, which keeps only the ids `COUNTRY_TO_REGION` maps at least
// one present article's country onto. A region with no articles behind it does not get a card:
// that was the whole bug (123yb692y1f) — every one of these used to render regardless, "Africa"
// included, and clicking any of them did nothing because nothing here was ever wired to the
// article list.
const regions: Card[] = [
	{
		id: "africa",
		name: "Africa",
		img: "https://images.unsplash.com/photo-1516426122078-c23e76319801?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
		icon: "nationalPark--chunky",
		accent: "forest",
	},
	{
		id: "asia",
		// "Asia", not "Southeast Asia". The id was always `asia` and `COUNTRY_TO_REGION` files every
		// Asian country here, Japan included — Japan being the corpus's largest country grouping.
		// Leaving the old label would have put 28 Japanese articles behind a card saying Southeast
		// Asia, which is a factual error on a travel site and a worse outcome than either dropping
		// them or widening the label. Renamed in all seven locales alongside this.
		name: "Asia",
		img: "https://images.unsplash.com/photo-1528181304800-259b08848526?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
		icon: "island--chunky",
		accent: "blue",
	},
	{
		id: "europe",
		name: "Europe",
		img: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
		icon: "city--chunky",
		accent: "green",
	},
	{
		id: "central-america",
		name: "Central America",
		img: "https://images.unsplash.com/photo-1518638150340-f706e86654de?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
		icon: "region--chunky",
		accent: "purple",
	},
	{
		id: "south-america",
		name: "South America",
		img: "https://images.unsplash.com/photo-1531968455001-5c5272a41129?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
		icon: "nationalPark--chunky",
		accent: "forest",
	},
	{
		id: "oceania",
		name: "Oceania",
		img: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
		icon: "island--chunky",
		accent: "blue",
	},
	{
		id: "north-america",
		name: "North America",
		img: "https://images.unsplash.com/photo-1500916434205-0c77489c6cf7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
		icon: "map--chunky",
		accent: "green",
	},
	{
		id: "middle-east",
		name: "Middle East",
		img: "https://images.unsplash.com/photo-1512632578888-169bbbc64f33?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
		icon: "sun--chunky",
		accent: "amber",
	},
];

// NO "SERIES" TAB. It used to render 10 hardcoded cards — Unique Landmarks, Five Days In…, Food
// Guides, and seven more — none of which were ever backed by anything: there is no `series`
// field on `BlogArticle` (models/BlogArticle.ts) or in the knowledge-base article frontmatter, so
// clicking one changed nothing — the same dead-click bug `onBrowseCard` used to have for "Where
// to?" too, before that tab was wired up to real data below. The two dead tabs looked identical
// from the outside but aren't the same fix: "Where to?" had data to derive from (`countrySlug`/
// `geo`) that nothing had connected yet, so it was wired up. "Series" has no data to derive from
// — no article carries a series identifier, so there is nothing to group by even in principle.
//
// It is removed rather than left hidden or wired to a no-op, for the same reason the "load more"
// button above is gone rather than disabled: a control that has stopped meaning anything is worse
// left in place than taken out, because it keeps looking like a decision someone made on purpose.
// Reintroducing this tab means adding a `series` field to the article schema and the knowledge
// base's frontmatter first (so it has something to derive from, the way `regionCards` derives
// from `countrySlug`/`geo` below) — not restoring this array.

// Country → region lookup for "Where to?". `regionCards` below only renders a region whose id
// appears here for at least one article's `countrySlug` (falling back to `geo`'s leading
// segment) — an unmapped country simply doesn't surface under any region, which is the safe
// failure: a card that filters to zero articles is worse than one that never existed (see the
// region-count note on `regionCards`).
//
// Covers every country the published English corpus carries today (verified against
// heroku_7bzt9drg.blog_articles, 2026-09-15: italy, france, netherlands, germany, japan,
// thailand, vietnam, philippines, indonesia, mexico, australia — 11 countries, not the 13 this
// ticket estimated; the other 2 didn't turn up in the live, non-`tier: landing` set no matter how
// the count was sliced). Add a country here when a new one starts publishing, not preemptively —
// an entry for a country nothing has been written about yet is untestable and risks being wrong
// in a way nothing would catch.
//
// "asia" holds every Asian country in the corpus, Japan included, rather than splitting out an
// "East Asia" this 8-region set has no card for. The alternative is dropping Japan's articles —
// currently the single largest country grouping — from "Where to?" entirely for the sake of a
// label that says "Southeast" more precisely than the id does. Mexico sits under
// "north-america", not "central-america", matching the existing continent grouping in
// `frontend/src/constants/countryToContinent.ts` rather than inventing a second convention for
// the same fact.
const COUNTRY_TO_REGION: Record<string, string> = {
	italy: "europe",
	france: "europe",
	netherlands: "europe",
	germany: "europe",
	japan: "asia",
	thailand: "asia",
	vietnam: "asia",
	philippines: "asia",
	indonesia: "asia",
	mexico: "north-america",
	australia: "oceania",
};

// Fallback imagery / accents / icons for CMS categories (which carry only a name).
const CATEGORY_IMG_CYCLE = [
	"https://images.unsplash.com/photo-1524492412937-b28074a5d7da?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
	"https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
	"https://images.unsplash.com/photo-1467269204594-9661b134dd2b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
	"https://images.unsplash.com/photo-1504674900247-0877df9cc836?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
	"https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
	"https://images.unsplash.com/photo-1493246507139-91e8fad9978e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
	"https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
	"https://images.unsplash.com/photo-1454496522488-7a8e488e8606?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
];
const CATEGORY_ICON_CYCLE: ChunkyIcon[] = [
	"destinations--chunky",
	"map--chunky",
	"city--chunky",
	"eatdrink--chunky",
	"heart--chunky",
	"money-bag--chunky",
	"island--chunky",
	"nationalPark--chunky",
];
const CATEGORY_ACCENT_CYCLE: AccentKey[] = ["pink", "green", "purple", "forest", "blue", "amber"];

const BROWSE_TILT = [
	styles.browseTilt0,
	styles.browseTilt1,
	styles.browseTilt2,
	styles.browseTilt3,
	styles.browseTilt4,
	styles.browseTilt5,
	styles.browseTilt6,
	styles.browseTilt7,
	styles.browseTilt8,
	styles.browseTilt9,
];
const THUMB_TILT = [styles.featuredThumb0, styles.featuredThumb1, styles.featuredThumb2];

// Per-category metadata for the post cards (icon + accent).
const POST_META: Record<string, { icon: ChunkyIcon; accent: AccentKey }> = {
	Destinations: { icon: "destinations--chunky", accent: "pink" },
	Itineraries: { icon: "map--chunky", accent: "green" },
	"City guides": { icon: "city--chunky", accent: "purple" },
	"Slow travel": { icon: "nationalPark--chunky", accent: "forest" },
	Inspiration: { icon: "heart--chunky", accent: "pink" },
	"Group travel": { icon: "person--chunky", accent: "purple" },
};
const POST_FALLBACK: { icon: ChunkyIcon; accent: AccentKey } = { icon: "destinations--chunky", accent: "green" };

type Post = { id: string; link: string; img?: string; category: string; countrySlug?: string; title: string; excerpt: string; readTime: string; author?: any };

// `attrs.countrySlug` is the plain country; `attrs.geo` additionally carries a destination
// ("italy/rome") and is read only when `countrySlug` itself is absent — both now flow through
// `kbArticleAsStrapi` in graphql/helpers/marketingSsrLoaders.ts unchanged, so this is the one
// place that decides which of the two wins. Neither is guaranteed: most of the corpus (app
// comparisons, budget guides, route-planning pieces with no destination) carries neither, and
// `undefined` here is what tells the region derivation below not to file the article anywhere.
const countryOf = (attrs: any): string | undefined => attrs?.countrySlug || (typeof attrs?.geo === "string" ? attrs.geo.split("/")[0] : undefined);

const pickImg = (headerImage: any) => {
	const f = headerImage?.data?.attributes?.formats;
	return f?.large?.url || f?.medium?.url || f?.small?.url || f?.thumbnail?.url || headerImage?.data?.attributes?.url;
};

// ── Recent-post card — contained, authored, with hover lift ──────────────────
function PostCard({ post, author, big = false }: { post: Post; author: Byline; big?: boolean }) {
	const meta = POST_META[post.category] ?? POST_FALLBACK;
	return (
		<a href={post.link} className={`${styles.postCard} ${ACCENT_CLASS[meta.accent]} ${big ? styles.postCardBig : ""}`}>
			<div className={`${styles.postMedia} ${big ? styles.postMediaBig : ""}`}>
				{/* `pickImg` returns undefined for an article with no header image, and React then
				    omits the attribute entirely — leaving an <img> with no src, which browsers
				    resolve against the page URL and screen readers announce as an image whose
				    description is the article title. Three of them were on production. The scrim
				    and the tag below still render, so the card keeps its shape; it just does not
				    claim to have a picture it does not have. */}
				{post.img && <img src={post.img} alt={post.title} loading="lazy" className={styles.postImg} />}
				<div className={styles.postScrim} />
				<span className={styles.postTag}>
					<Icon name={meta.icon} fill="currentColor" size={13} />
					<span className={styles.postTagText}>{post.category}</span>
				</span>
			</div>

			<div className={`${styles.postBody} ${big ? styles.postBodyBig : ""}`}>
				{big && <span className={styles.postKicker}>Latest story</span>}
				<p className={`${styles.postTitle} ${big ? styles.postTitleBig : ""}`}>{post.title}</p>
				<p className={`${styles.postExcerpt} ${big ? styles.postExcerptBig : ""}`}>{post.excerpt}</p>

				<div className={styles.postMeta}>
					<span className={styles.postAvatar}>
						<img src={author.avatar} alt={author.name} loading="lazy" />
					</span>
					<span className={styles.postAuthor}>{author.name}</span>
					{/* Both the separator and the icon are inside the guard: an article with no
					    reading time used to be impossible, because one was invented for it. Now that
					    the number is real it can be absent, and a lone calendar icon beside a stray
					    dot reads as a broken card rather than a missing field. */}
					{post.readTime && (
						<>
							<span className={styles.postDot} />
							<span className={styles.postReadTime}>
								<Icon name="calendar--chunky" fill="currentColor" size={12} />
								{post.readTime}
							</span>
						</>
					)}
					<Icon name="external-link" size={8} className={styles.postArrow} />
				</div>
			</div>
		</a>
	);
}

// ── Browse card — photo tile with tilted icon badge (DestinationCard style) ──
// A <button>, not an <a href="#">: every card here filters the list in place rather than
// navigating anywhere, on both tabs. An anchor with a cancelled default only *looks* like it has
// nowhere to go — middle-click, "open in new tab" and crawlers all ignore `preventDefault` and
// try the href literally, landing on "#" (see the header comment on planGate.tsx for the same
// rule applied to the plan CTA). A control with no page behind it says so in its element type.
function BrowseCard({ card, tiltClass, onClick }: { card: Card; tiltClass: string; onClick?: () => void }) {
	return (
		<button type="button" onClick={onClick} className={`${styles.browseCard} ${tiltClass} ${ACCENT_CLASS[card.accent]}`}>
			<div className={styles.browseInner}>
				<img src={card.img} alt={card.name} loading="lazy" className={styles.browseImg} />
				<div className={styles.browseScrim} />
				<span className={styles.browseBadge}>
					<IconTile icon={card.icon} bgClass={`${styles.iconTileBgCard} ${styles.iconTileBgAccent}`} />
				</span>
				<div className={styles.browseFooter}>
					<p className={styles.browseName}>{card.name}</p>
					<Icon name="external-link" size={8} className={styles.browseArrow} />
				</div>
			</div>
		</button>
	);
}

type Tab = "where" | "categories";

const Blog = ({ initialData, lang }: BlogProps = {}) => {
	// `lang` is only threaded in by MarketingShell (SSR + hydration). The SPA app router
	// (WebsiteRoutes.tsx) renders <Blog> via `component={Blog}` with no prop, so fall back to
	// `useMarketingLang()` — same router-then-window resolution this used to hand-roll here,
	// now shared with every other marketing component (see its doc comment).
	const hookLang = useMarketingLang();
	const currentLang = lang || hookLang;
	const langPrefix = currentLang === "en" ? "" : `/${currentLang}`;
	const translations = getBlogTranslations(currentLang);
	const [articles, setArticles] = useState<any[]>(initialData?.articles || []);
	const [spotlight, setSpotlight] = useState<any>(initialData?.spotlight || {});
	const [categories, setCategories] = useState<any[]>(initialData?.categories || []);
	const [activeCategory, setActiveCategory] = useState("all");
	// A region id from COUNTRY_TO_REGION, or null for "no region filter". Kept independent of
	// `activeCategory` rather than folded into one union: the two tabs' filters are mutually
	// exclusive at any given moment (see `onBrowseCard`), but each still needs its own "cleared"
	// state to fall back to when the other one is active.
	const [activeRegion, setActiveRegion] = useState<string | null>(null);

	const [tab, setTab] = useState<Tab>("where");
	const [query, setQuery] = useState("");
	const recentRef = useRef<HTMLDivElement>(null);

	// Client-side nav here (e.g. the homepage's "All articles" link) keeps the
	// scroll offset from the previous page, so the blog header renders off-screen.
	useEffect(() => {
		if (typeof window !== "undefined") window.scrollTo(0, 0);
	}, []);

	// The blog index's list — spotlight, categories and articles — in the one query
	// `loadBlogList` builds `initialData` from server-side (graphql/schemas/blog.ts). `skip:
	// !!initialData` means a hard load costs no extra round trip: the SSR Apollo client is a
	// no-op link that never resolves (ssrApolloClient.ts), so a non-skipped query during SSR
	// would sit in `loading` forever and render nothing — the same guard `EXPLORE_GUIDE` uses
	// in DiscoverCountryPage.tsx.
	//
	// This single query replaces three effects that used to read from Strapi directly on every
	// client-side navigation into this page: a spotlight fetch, a page-1 fetch (pageSize 25),
	// and a category effect that refetched Strapi's list whenever the filter cleared. Strapi
	// has 200+ non-spotlight articles and the client only ever kept the newest 25 of them — a
	// different, larger corpus than the knowledge-base list SSR renders, which is why a
	// client-side nav into /blog could show a spotlight article the search then denied ever
	// existed (123yb692z6e). The category effect existed to work around the same split: a chip
	// built from the knowledge base's articles, refetching Strapi's differently-scoped list
	// (123yb691r8m). With one corpus for the whole page there is nothing left for that effect
	// to fetch — filtering was already local — so it is gone rather than repointed.
	const { data: blogListData } = useQuery(BLOG_LIST, { variables: { language: currentLang }, skip: !!initialData });

	useEffect(() => {
		const list = blogListData?.blogList;
		if (!list) return;
		setSpotlight(list.spotlight || {});
		setCategories(list.categories || []);
		setArticles(list.articles || []);
	}, [blogListData]);

	// ── Derived view data ───────────────────────────────────────────────────────
	const postList: Post[] = articles.map((a, i) => {
		const at = a?.attributes || {};
		return {
			id: String(a?.id ?? i),
			link: at.slug ? `${langPrefix}/blog/${at.slug}` : "#",
			img: pickImg(at.headerImage),
			category: at.blogCategories?.data?.[0]?.attributes?.name || "Destinations",
			countrySlug: countryOf(at),
			title: at.title || "",
			excerpt: at.excerpt || "",
			// `readingTime`, which is what the loader actually sends (`kbArticleAsStrapi` maps the
			// document's `readingMinutes` to it). This read `at.readTime` — a key nothing sets —
			// so the `||` fired on every card and every read time on the index was INVENTED from
			// the array position: `5 + (i % 6)`, cycling 5..10 down the page. The same article
			// showed 8 minutes as the Editor's Pick and 5 in the strip purely because its index
			// differed (123yb6937h5). The real figures were in the payload the whole time —
			// Northern Italy is 14 minutes, not 8.
			//
			// No fallback. A made-up number is worse than no number: it looks like data.
			readTime: at.readingTime ? `${at.readingTime} ${t(translations, "blog.minRead", "min read")}` : "",
			author: at.author || null,
		};
	});

	// Region and category filters never compound — selecting one clears the other (`onBrowseCard`)
	// — so a single active-filter branch is enough: at most one of `activeRegion` /
	// `activeCategory !== "all"` is ever true at a time.
	const byActiveFilter = activeRegion
		? postList.filter((p) => COUNTRY_TO_REGION[p.countrySlug || ""] === activeRegion)
		: activeCategory === "all"
			? postList
			: postList.filter((p) => p.category.toLowerCase() === activeCategory.toLowerCase());
	const filteredPosts = query
		? byActiveFilter.filter(
				(p) =>
					p.title.toLowerCase().includes(query.toLowerCase()) ||
					p.category.toLowerCase().includes(query.toLowerCase()) ||
					p.excerpt.toLowerCase().includes(query.toLowerCase())
			)
		: byActiveFilter;

	const categoryCards: Card[] = categories.map((c, i) => {
		const name = c?.attributes?.name || "";
		const meta = POST_META[name];
		return {
			id: String(c?.id ?? name),
			name,
			img: CATEGORY_IMG_CYCLE[i % CATEGORY_IMG_CYCLE.length],
			icon: meta?.icon ?? CATEGORY_ICON_CYCLE[i % CATEGORY_ICON_CYCLE.length],
			accent: meta?.accent ?? CATEGORY_ACCENT_CYCLE[i % CATEGORY_ACCENT_CYCLE.length],
		};
	});

	// Which of the 8 region cards actually render: only ids that COUNTRY_TO_REGION maps at least
	// one present article's country onto. `spotlight` is folded in alongside `postList` because
	// `categoryCards` above counts it too — both are built from the loader's full [pick, ...rest]
	// set before the spotlight is split out into its own section, and the "Where to?" count
	// should describe the same corpus the "Categories" count already does.
	const presentRegionIds = new Set(
		[countryOf(spotlight), ...postList.map((p) => p.countrySlug)].map((slug) => COUNTRY_TO_REGION[slug || ""]).filter(Boolean)
	);
	const regionCards: Card[] = regions.filter((r) => presentRegionIds.has(r.id));

	// CMS category names arrive already localised; the hardcoded region names don't, so map them
	// through the blog dictionary (keyed by card id, English text as the fallback).
	const localiseCard = (card: Card): Card => ({ ...card, name: t(translations, `blog.browse.${card.id}`, card.name) });
	const activeCards: Card[] = (tab === "where" ? regionCards : categoryCards).map((c) => (tab === "categories" ? c : localiseCard(c)));

	const tabs: { id: Tab; label: string; icon: ChunkyIcon; count: number }[] = [
		{ id: "where", label: t(translations, "blog.tabs.where", "Where to?"), icon: "marker--chunky", count: regionCards.length },
		{ id: "categories", label: t(translations, "blog.tabs.categories", "Categories"), icon: "heart--chunky", count: categoryCards.length },
	];

	// One active filter at a time: picking a region clears whatever category was active and vice
	// versa, rather than intersecting them. Nothing in the corpus makes an intersection a natural
	// operation to offer — categories are editorial ("Itineraries", "City guides"), regions are
	// geographic, and there is no promise every combination has an article behind it, which is
	// exactly the empty-result trap `regionCards`/`categoryCards` already exist to avoid on the
	// browse grid itself.
	const onBrowseCard = (card: Card) => () => {
		if (tab === "categories") {
			setActiveCategory(card.name);
			setActiveRegion(null);
		} else if (tab === "where") {
			// `card.id`, not `card.name` — `localiseCard` rewrites `name` per locale above, and
			// COUNTRY_TO_REGION and `presentRegionIds` are both keyed on the untouched region id.
			setActiveRegion(card.id);
			setActiveCategory("all");
		}
		recentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	// Localised label for the active region, for the "Showing articles in …" line below — the
	// same dictionary lookup `localiseCard` applies to the browse card itself.
	const activeRegionLabel = activeRegion
		? t(translations, `blog.browse.${activeRegion}`, regions.find((r) => r.id === activeRegion)?.name || activeRegion)
		: "";

	// ── Editor's pick (spotlight) ──────────────────────────────────────────────
	const spot = spotlight && spotlight.title ? spotlight : null;
	const spotCategory = spot?.blogCategories?.data?.[0]?.attributes?.name || "Itineraries";
	const spotMeta = POST_META[spotCategory] || { icon: "map--chunky" as ChunkyIcon, accent: "pink" as AccentKey };
	const spotImage = pickImg(spot?.headerImage);
	const spotLink = spot?.slug ? `${langPrefix}/blog/${spot.slug}` : "#";
	const spotThumbs = postList
		.slice(0, 3)
		.map((p) => p.img)
		.filter(Boolean) as string[];
	const spotReadTime = spot?.readTime || `8 ${t(translations, "blog.minRead", "min read")}`;

	// NO "LOAD MORE". `blogList` returns the whole list in one response, and the loader it wraps
	// reports `total` as the length of what it returned (`loadKbBlogList`, capped at 50), so
	// `articles.length < total` was already false on every live page — the button never appeared.
	//
	// It is removed rather than left hidden because it had stopped working: paging used to fetch
	// page 2 from Strapi, and with one corpus there is nothing to fetch. On an environment where
	// the knowledge-base sync has not run, `loadBlogList` falls back to Strapi and reports a
	// `total` of a few hundred against 25 returned — so the button WOULD have rendered there, and
	// done nothing at all.
	//
	// If the corpus ever outgrows that 50-item cap, pagination belongs in `blogList`
	// (`graphql/schemas/blog.ts`) as `limit`/`offset` arguments, not back here.

	return (
		<div id="stippl-blog" className={styles.page}>
			<Navigation impact={true} langMenu={<LanguageSwitcher type="list" />} />

			{/* ── Dark browse hero ─────────────────────────────────────────────── */}
			<div className={styles.hero}>
				<SDecor className={`${styles.sDecor} ${styles.sDecorTop}`} />
				<SDecor className={`${styles.sDecor} ${styles.sDecorBottom}`} />

				<div className={`${styles.container} ${styles.heroInner}`}>
					<div className={styles.heroTop}>
						<div>
							<div className={styles.eyebrow}>
								<Icon name="destinations--chunky" fill="currentColor" size={15} />
								<span>{t(translations, "blog.hero.eyebrow", "The Stippl journal")}</span>
							</div>
							<h1 className={styles.heroTitle}>
								{t(translations, "blog.hero.titleLine1", "Every guide, itinerary")} <br />
								{t(translations, "blog.hero.titleLine2", "and story in one place")}
							</h1>
						</div>
					</div>

					<div className={styles.toolbar}>
						<div className={styles.tabs}>
							{tabs.map((t) => {
								const isActive = tab === t.id;
								return (
									<button key={t.id} id={`blog-tab-${t.id}`} onClick={() => setTab(t.id)} className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}>
										<Icon name={t.icon} fill="currentColor" size={16} />
										{t.label}
										<span className={styles.tabCount}>{t.count}</span>
									</button>
								);
							})}
						</div>

						<div className={styles.search}>
							<Search size={18} />
							<input
								id="blog-search"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder={t(translations, "blog.search.placeholder", "Search articles…")}
								className={styles.searchInput}
							/>
						</div>
					</div>

					<div className={styles.browseGrid}>
						{activeCards.map((card, i) => (
							<BrowseCard key={card.id} card={card} tiltClass={BROWSE_TILT[i % BROWSE_TILT.length]} onClick={onBrowseCard(card)} />
						))}
					</div>
				</div>
			</div>

			{/* ── Editor's pick ────────────────────────────────────────────────── */}
			{spot && (
				<section className={styles.sectionFeatured}>
					<div className={styles.sectionLabel}>
						<IconTile icon="heart--chunky" bgClass={styles.iconTileBgPink} />
						<span className={styles.sectionLabelText}>{t(translations, "blog.editorsPick", "Editor's pick")}</span>
					</div>
					<a href={spotLink} className={`${styles.featuredCard} ${ACCENT_CLASS[spotMeta.accent]}`}>
						<div className={styles.featuredMedia}>
							<img src={spotImage} alt={spot.title} {...({ fetchpriority: "high" } as any)} className={styles.featuredImg} />
						</div>
						<div className={styles.featuredBody}>
							<div className={styles.featuredMetaRow}>
								<span className={styles.featuredChip}>
									<Icon name={spotMeta.icon} fill="currentColor" size={14} />
									<span>{spotCategory}</span>
								</span>
								<span className={styles.featuredReadTime}>{spotReadTime}</span>
							</div>
							<h2 className={styles.featuredTitle}>{spot.title}</h2>
							<p className={styles.featuredExcerpt}>{spot.excerpt}</p>
							{spotThumbs.length > 0 && (
								<div className={styles.featuredThumbs}>
									{spotThumbs.map((t, i) => (
										<span key={i} className={`${styles.featuredThumb} ${THUMB_TILT[i]}`}>
											<img src={t} alt="" loading="lazy" />
										</span>
									))}
								</div>
							)}
							<span className={styles.featuredCta}>
								<span className={styles.featuredCtaIcon}>
									<Icon name="lineArrowRight" fill="currentColor" size={14} />
								</span>
								{t(translations, "blog.featured.cta", "Read the guide")}
							</span>
						</div>
					</a>
				</section>
			)}

			{/* ── Recent posts ─────────────────────────────────────────────────── */}
			<section className={styles.sectionRecent} ref={recentRef}>
				<div className={styles.recentHead}>
					<div>
						<div className={styles.sectionLabel}>
							<IconTile icon="calendar--chunky" bgClass={styles.iconTileBgMint} />
							<span className={styles.sectionLabelText}>{t(translations, "blog.recent.label", "Fresh off the map")}</span>
						</div>
						<h2 className={styles.recentTitle}>{t(translations, "blog.recent.title", "Recent posts")}</h2>
						<p className={styles.recentSub}>
							{activeRegion
								? `${t(translations, "blog.recent.showingIn", "Showing articles in")} “${activeRegionLabel}”.`
								: activeCategory === "all"
									? t(translations, "blog.recent.sub", "Fresh guides, itineraries and inspiration for your next trip.")
									: `${t(translations, "blog.recent.showingIn", "Showing articles in")} “${activeCategory}”.`}
						</p>
					</div>
					{activeCategory === "all" && !activeRegion ? (
						<a href={`${langPrefix}/blog`} className={styles.viewAll}>
							{t(translations, "blog.recent.viewAll", "View all")}
							<Icon name="lineArrowRight" fill="currentColor" size={14} />
						</a>
					) : (
						<button
							onClick={() => {
								setActiveCategory("all");
								setActiveRegion(null);
							}}
							className={styles.viewAll}
						>
							{t(translations, "blog.recent.clearFilter", "Clear filter")}
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
								<path d="M18 6 6 18M6 6l12 12" />
							</svg>
						</button>
					)}
				</div>

				{filteredPosts.length === 0 ? (
					<p className={styles.empty}>
						{query ? `${t(translations, "blog.empty.noMatch", "No articles match")} “${query}”.` : t(translations, "blog.empty.none", "No articles yet.")}
					</p>
				) : (
					<div className={styles.postGrid}>
						{filteredPosts.map((post, i) => (
							<div key={post.id} className={i === 0 ? styles.postBig : styles.postGridItem}>
								<PostCard post={post} author={bylineOf(post.author)} big={i === 0} />
							</div>
						))}
					</div>
				)}

			</section>

			<Footer />
			<DownloadAppQrCard />
		</div>
	);
};

export default Blog;
